// src/components/dashboard/Dashboard.tsx

import React, {useMemo, useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    CalendarMonth as LessonsIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    CurrencyRuble as MoneyIcon,
    Group as GroupsIcon,
    People as StudentsIcon,
    Schedule as ScheduleIcon,
    Today as TodayIcon,
    TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {addMonths, format, isAfter, isSameDay, isSameMonth, parseISO, subMonths,} from 'date-fns';
import ru from 'date-fns/locale/ru';
import {useAppContext} from '../../context/AppContext';
import {useAuth} from '../../context/AuthContext';
import {isStudent, isTeacherOrAdmin} from '../../utils/roleUtils';

const Dashboard: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {students, groups, lessons} = useAppContext();
    const {user} = useAuth();

    const userIsTeacher = isTeacherOrAdmin(user);
    const userIsStudent = isStudent(user);

    // Выбранный месяц для статистики
    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());

    // ===== СТАТИСТИКА =====

    const activeStudents = useMemo(() =>
            students.filter(s => s.isActive),
        [students]
    );

    const totalGroups = groups.length;

    // Уроки за выбранный месяц (уже отфильтрованы по роли в AppContext)
    const lessonsThisMonth = useMemo(() => {
        return lessons.filter(lesson => {
            try {
                const lessonDate = parseISO(lesson.date);
                return isSameMonth(lessonDate, selectedMonth);
            } catch {
                return false;
            }
        });
    }, [lessons, selectedMonth]);

    // Доход — только для TEACHER/ADMIN
    const monthIncome = useMemo(() => {
        let total = 0;

        lessonsThisMonth.forEach(lesson => {
            // ← Пропускаем бесплатные уроки
            if (lesson.isFree) return;

            if (lesson.studentId) {
                const student = students.find(s => s.id === lesson.studentId);
                if (student) {
                    total += student.pricePerLesson;
                }
            } else if (lesson.groupId) {
                const group = groups.find(g => g.id === lesson.groupId);
                if (group && group.studentIds) {
                    group.studentIds.forEach(studentId => {
                        const student = students.find(s => s.id === studentId && s.isActive);
                        if (student) {
                            total += student.pricePerLesson;
                        }
                    });
                }
            }
        });

        return total;
    }, [lessonsThisMonth, students, groups]);

    // ===== БЛИЖАЙШИЕ УРОКИ =====

    const upcomingLessons = useMemo(() => {
        const now = new Date();
        return lessons
            .filter(lesson => {
                try {
                    const lessonDate = parseISO(lesson.date);
                    return isAfter(lessonDate, now) || isSameDay(lessonDate, now);
                } catch {
                    return false;
                }
            })
            .sort((a, b) => {
                try {
                    return parseISO(a.date).getTime() - parseISO(b.date).getTime();
                } catch {
                    return 0;
                }
            })
            .slice(0, 5);
    }, [lessons]);

    // ===== УРОКИ СЕГОДНЯ =====

    const todayLessons = useMemo(() => {
        const now = new Date();
        return lessons.filter(lesson => {
            try {
                return isSameDay(parseISO(lesson.date), now);
            } catch {
                return false;
            }
        }).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    }, [lessons]);

    // ===== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ =====

    const formatPrice = (price: number): string => {
        const rounded = Math.round(price * 100) / 100;
        return rounded.toLocaleString('ru-RU', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    };

    const getLessonTitle = (lesson: any) => {
        if (userIsStudent) {
            return lesson.studentId ? 'Индивидуальный урок' : 'Групповой урок';
        }

        if (lesson.studentId) {
            const student = students.find(s => s.id === lesson.studentId);
            return student ? student.fullName : 'Ученик удален';
        }
        if (lesson.groupId) {
            const group = groups.find(g => g.id === lesson.groupId);
            return group ? group.name : 'Группа удалена';
        }
        return 'Без ученика';
    };

    const getLessonColor = (lesson: any) => {
        if (lesson.studentId) return theme.palette.primary.main;
        if (lesson.groupId) return theme.palette.secondary.main;
        return theme.palette.grey[500];
    };

    const formatLessonDate = (dateStr: string): string => {
        try {
            const date = parseISO(dateStr);
            const today = new Date();
            if (isSameDay(date, today)) return 'Сегодня';
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (isSameDay(date, tomorrow)) return 'Завтра';
            return format(date, 'd MMMM, EEEE', {locale: ru});
        } catch {
            return 'Некорректная дата';
        }
    };

    // ===== ПЕРЕКЛЮЧАТЕЛЬ МЕСЯЦА =====

    const handlePrevMonth = () => setSelectedMonth(prev => subMonths(prev, 1));
    const handleNextMonth = () => setSelectedMonth(prev => addMonths(prev, 1));
    const handleCurrentMonth = () => setSelectedMonth(new Date());

    const isCurrentMonth = isSameMonth(selectedMonth, new Date());

    // ===== СТАТИСТИЧЕСКИЕ КАРТОЧКИ =====

    const stats = useMemo(() => {
        if (userIsStudent) {
            // STUDENT — только уроки за месяц
            return [
                {
                    title: 'Уроков за месяц',
                    value: lessonsThisMonth.length,
                    icon: <LessonsIcon sx={{fontSize: {xs: 32, sm: 40}}}/>,
                    color: theme.palette.success.main,
                    bgColor: theme.palette.success.light + '20',
                    link: '/calendar',
                    subtitle: format(selectedMonth, 'LLLL yyyy', {locale: ru}),
                },
            ];
        }

        // TEACHER/ADMIN — полная статистика
        return [
            {
                title: 'Активных учеников',
                value: activeStudents.length,
                icon: <StudentsIcon sx={{fontSize: {xs: 32, sm: 40}}}/>,
                color: theme.palette.primary.main,
                bgColor: theme.palette.primary.light + '20',
                link: '/students',
                subtitle: students.length > 0 ? `всего ${students.length}` : 'нет учеников',
            },
            {
                title: 'Групп',
                value: totalGroups,
                icon: <GroupsIcon sx={{fontSize: {xs: 32, sm: 40}}}/>,
                color: theme.palette.secondary.main,
                bgColor: theme.palette.secondary.light + '20',
                link: '/groups',
                subtitle: totalGroups > 0 ? 'активных групп' : 'нет групп',
            },
            {
                title: 'Уроков за месяц',
                value: lessonsThisMonth.length,
                icon: <LessonsIcon sx={{fontSize: {xs: 32, sm: 40}}}/>,
                color: theme.palette.success.main,
                bgColor: theme.palette.success.light + '20',
                link: '/calendar',
                subtitle: format(selectedMonth, 'LLLL yyyy', {locale: ru}),
            },
            {
                title: 'Доход за месяц',
                value: `${formatPrice(monthIncome)} ₽`,
                icon: <MoneyIcon sx={{fontSize: {xs: 32, sm: 40}}}/>,
                color: theme.palette.warning.main,
                bgColor: theme.palette.warning.light + '20',
                link: '/calendar',
                subtitle: isCurrentMonth ? 'предварительный' : 'за прошедший месяц',
            },
        ];
    }, [userIsStudent, lessonsThisMonth, activeStudents, totalGroups, monthIncome, selectedMonth, theme, students, isCurrentMonth]);

    return (
        <Box>
            {/* Заголовок */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: {xs: 'flex-start', sm: 'center'},
                mb: 3,
                flexDirection: {xs: 'column', sm: 'row'},
                gap: {xs: 2, sm: 0},
            }}>
                <Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{fontWeight: 500}}>
                        📊 Дашборд
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {format(new Date(), 'EEEE, d MMMM yyyy', {locale: ru})}
                    </Typography>
                </Box>
                <Box sx={{display: 'flex', gap: 1, width: {xs: '100%', sm: 'auto'}}}>
                    <Button
                        variant="outlined"
                        startIcon={<TodayIcon/>}
                        onClick={() => navigate('/calendar')}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{flex: {xs: 1, sm: 'none'}}}
                    >
                        Календарь
                    </Button>
                    {userIsTeacher && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon/>}
                            onClick={() => navigate('/students')}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{flex: {xs: 1, sm: 'none'}}}
                        >
                            Ученик
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Переключатель месяца */}
            <Paper sx={{p: 2, mb: 3}}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexDirection: {xs: 'column', sm: 'row'},
                    gap: {xs: 1.5, sm: 0},
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Typography variant="body2" color="text.secondary" sx={{mr: 1}}>
                            Статистика за:
                        </Typography>
                        <IconButton onClick={handlePrevMonth} size="small">
                            <ChevronLeftIcon/>
                        </IconButton>
                        <Typography
                            variant="subtitle1"
                            sx={{minWidth: {xs: '150px', sm: '200px'}, textAlign: 'center', fontWeight: 600}}
                        >
                            {format(selectedMonth, 'LLLL yyyy', {locale: ru})}
                        </Typography>
                        <IconButton onClick={handleNextMonth} size="small">
                            <ChevronRightIcon/>
                        </IconButton>
                    </Box>
                    <Button
                        variant={isCurrentMonth ? 'outlined' : 'contained'}
                        size="small"
                        onClick={handleCurrentMonth}
                        disabled={isCurrentMonth}
                        fullWidth={isMobile}
                    >
                        {isCurrentMonth ? 'Текущий месяц' : 'Вернуться к текущему'}
                    </Button>
                </Box>
            </Paper>

            {/* Карточки статистики */}
            <Grid container spacing={{xs: 2, sm: 3}} sx={{mb: 4}}>
                {stats.map((stat, index) => (
                    <Grid
                        key={index}
                        size={{
                            xs: 12,
                            sm: userIsStudent ? 12 : 6,
                            lg: userIsStudent ? 12 : 3,
                        }}
                    >
                        <Card
                            sx={{
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: {xs: 'none', sm: 'translateY(-4px)'},
                                    boxShadow: theme.shadows[8],
                                },
                            }}
                            onClick={() => navigate(stat.link)}
                        >
                            <CardContent sx={{p: {xs: 2, sm: 3}}}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: {xs: 'column', sm: 'row'},
                                    justifyContent: 'space-between',
                                    alignItems: {xs: 'flex-start', sm: 'flex-start'},
                                    gap: 1,
                                }}>
                                    <Box sx={{flex: 1}}>
                                        <Typography variant="body2" color="text.secondary"
                                                    sx={{mb: 0.5, fontSize: {xs: '0.7rem', sm: '0.875rem'}}}>
                                            {stat.title}
                                        </Typography>
                                        <Typography variant={isMobile ? 'h6' : 'h4'}
                                                    sx={{fontWeight: 600, color: stat.color, mb: 0.5}}>
                                            {stat.value}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary"
                                                    sx={{fontSize: {xs: '0.65rem', sm: '0.75rem'}}}>
                                            {stat.subtitle}
                                        </Typography>
                                    </Box>
                                    {!isMobile && (
                                        <Avatar sx={{bgcolor: stat.bgColor, color: stat.color, width: 56, height: 56}}>
                                            {stat.icon}
                                        </Avatar>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Две колонки */}
            <Grid container spacing={{xs: 2, sm: 3}}>
                {/* Уроки сегодня */}
                <Grid size={{xs: 12, lg: 6}}>
                    <Paper sx={{p: {xs: 2, sm: 3}, height: '100%'}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <TodayIcon color="primary" fontSize={isMobile ? 'small' : 'medium'}/>
                                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{fontWeight: 600}}>
                                    Уроки сегодня
                                </Typography>
                            </Box>
                            <Chip label={todayLessons.length} color="primary" size="small"/>
                        </Box>
                        <Divider sx={{mb: 2}}/>

                        {todayLessons.length === 0 ? (
                            <Box sx={{py: {xs: 3, sm: 4}, textAlign: 'center', color: 'text.secondary'}}>
                                <ScheduleIcon sx={{fontSize: 48, opacity: 0.3, mb: 1}}/>
                                <Typography variant="body2">На сегодня уроков нет</Typography>
                                <Typography variant="caption">Отдыхайте! 🎉</Typography>
                            </Box>
                        ) : (
                            <List sx={{p: 0}}>
                                {todayLessons.map((lesson) => (
                                    <ListItem
                                        key={lesson.id}
                                        sx={{
                                            px: {xs: 1.5, sm: 2},
                                            py: {xs: 1, sm: 1.5},
                                            mb: 1,
                                            borderRadius: 1,
                                            bgcolor: 'action.hover',
                                            borderLeft: `4px solid ${getLessonColor(lesson)}`,
                                            '&:hover': {bgcolor: 'action.selected'},
                                        }}
                                    >
                                        <ListItemAvatar sx={{minWidth: {xs: 40, sm: 56}}}>
                                            <Avatar sx={{
                                                bgcolor: getLessonColor(lesson) + '20',
                                                color: getLessonColor(lesson),
                                                width: {xs: 32, sm: 40},
                                                height: {xs: 32, sm: 40},
                                            }}>
                                                <ScheduleIcon fontSize="small"/>
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                        {lesson.startTime?.slice(0, 5)} - {lesson.endTime?.slice(0, 5)}
                                                    </Typography>
                                                    {userIsTeacher && (
                                                        <Chip
                                                            label={lesson.studentId ? 'Инд.' : 'Гр.'}
                                                            size="small"
                                                            color={lesson.studentId ? 'primary' : 'secondary'}
                                                            sx={{height: 18, fontSize: '0.65rem'}}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    {userIsTeacher && (
                                                        <Typography variant="body2" color="text.primary">
                                                            {getLessonTitle(lesson)}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {lesson.topic}
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}

                        {todayLessons.length > 0 && (
                            <Button fullWidth variant="text" onClick={() => navigate('/calendar')} sx={{mt: 1}}>
                                Открыть календарь
                            </Button>
                        )}
                    </Paper>
                </Grid>

                {/* Ближайшие уроки */}
                <Grid size={{xs: 12, lg: 6}}>
                    <Paper sx={{p: {xs: 2, sm: 3}, height: '100%'}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <TrendingUpIcon color="secondary" fontSize={isMobile ? 'small' : 'medium'}/>
                                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{fontWeight: 600}}>
                                    Ближайшие уроки
                                </Typography>
                            </Box>
                            <Chip label={upcomingLessons.length} color="secondary" size="small"/>
                        </Box>
                        <Divider sx={{mb: 2}}/>

                        {upcomingLessons.length === 0 ? (
                            <Box sx={{py: {xs: 3, sm: 4}, textAlign: 'center', color: 'text.secondary'}}>
                                <ScheduleIcon sx={{fontSize: 48, opacity: 0.3, mb: 1}}/>
                                <Typography variant="body2">Нет запланированных уроков</Typography>
                                <Typography variant="caption">Добавьте урок в календаре</Typography>
                            </Box>
                        ) : (
                            <List sx={{p: 0}}>
                                {upcomingLessons.map((lesson) => (
                                    <ListItem
                                        key={lesson.id}
                                        sx={{
                                            px: {xs: 1.5, sm: 2},
                                            py: {xs: 1, sm: 1.5},
                                            mb: 1,
                                            borderRadius: 1,
                                            bgcolor: 'action.hover',
                                            borderLeft: `4px solid ${getLessonColor(lesson)}`,
                                            '&:hover': {bgcolor: 'action.selected'},
                                        }}
                                    >
                                        <ListItemAvatar sx={{minWidth: {xs: 40, sm: 56}}}>
                                            <Avatar sx={{
                                                bgcolor: getLessonColor(lesson) + '20',
                                                color: getLessonColor(lesson),
                                                width: {xs: 32, sm: 40},
                                                height: {xs: 32, sm: 40},
                                            }}>
                                                <ScheduleIcon fontSize="small"/>
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                        {formatLessonDate(lesson.date)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {lesson.startTime?.slice(0, 5)} - {lesson.endTime?.slice(0, 5)}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    {userIsTeacher && (
                                                        <Typography variant="body2" color="text.primary">
                                                            {getLessonTitle(lesson)}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {lesson.topic}
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}

                        {upcomingLessons.length > 0 && (
                            <Button fullWidth variant="text" color="secondary" onClick={() => navigate('/calendar')}
                                    sx={{mt: 1}}>
                                Все уроки
                            </Button>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Прогресс активных учеников — только для TEACHER/ADMIN */}
            {userIsTeacher && activeStudents.length > 0 && (
                <Paper sx={{p: {xs: 2, sm: 3}, mt: {xs: 2, sm: 3}}}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        flexDirection: {xs: 'column', sm: 'row'},
                        gap: {xs: 1, sm: 0},
                    }}>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{fontWeight: 600}}>
                            Активность учеников
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {activeStudents.length} активных из {students.length}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={students.length > 0 ? (activeStudents.length / students.length) * 100 : 0}
                        sx={{height: 10, borderRadius: 5, mb: 1}}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {students.length > 0
                            ? `${Math.round((activeStudents.length / students.length) * 100)}% учеников активны`
                            : 'Нет учеников'}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default Dashboard;